import React from 'react';
import { Box, Container, Typography, Chip, Button, useTheme, alpha, Paper, Grid } from '@mui/material';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SEO, { createBreadcrumbSchema } from '../../components/SEO';

// Full blog post content
const blogContent = {
    'working-with-json-complete-guide': {
        title: 'The Complete Guide to Working with JSON in 2026',
        category: 'JSON',
        author: 'UmbrellaCo Team',
        date: 'February 5, 2026',
        readTime: '8 min read',
        image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1200',
        content: `
## What is JSON?

**JSON (JavaScript Object Notation)** is a lightweight data interchange format that's easy for humans to read and write, and easy for machines to parse and generate. It has become the de facto standard for data exchange on the web.

### Key Characteristics of JSON

- **Human-readable**: Clear, simple syntax
- **Language-independent**: Works with virtually any programming language
- **Lightweight**: Minimal overhead compared to XML
- **Self-describing**: Data structure is obvious from the format

## JSON Syntax Basics

JSON supports the following data types:

\`\`\`json
{
  "string": "Hello World",
  "number": 42,
  "boolean": true,
  "null": null,
  "array": [1, 2, 3],
  "object": {
    "nested": "value"
  }
}
\`\`\`

## Common JSON Operations

### 1. Parsing JSON

In JavaScript, use \`JSON.parse()\` to convert a JSON string to an object:

\`\`\`javascript
const jsonString = '{"name": "John", "age": 30}';
const obj = JSON.parse(jsonString);
console.log(obj.name); // "John"
\`\`\`

### 2. Stringifying Objects

Use \`JSON.stringify()\` to convert an object to a JSON string:

\`\`\`javascript
const obj = { name: "John", age: 30 };
const jsonString = JSON.stringify(obj, null, 2);
\`\`\`

### 3. Validating JSON

Always validate JSON before processing. Our [JSON Viewer](/json-tools) tool can help you validate and format JSON instantly.

## Best Practices

1. **Always validate** incoming JSON data
2. **Use consistent naming conventions** (camelCase is common)
3. **Keep payloads minimal** - only include necessary data
4. **Document your JSON schemas** for API contracts
5. **Handle errors gracefully** when parsing fails

## Conclusion

JSON remains the most popular data format for web APIs and configuration files. Master these fundamentals, and you'll be well-equipped to handle any JSON-related task.

---

**Try our free [JSON Tools](/json-tools)** to validate, format, and convert JSON instantly!
    `,
    },
    'csv-to-json-conversion-tips': {
        title: 'CSV to JSON Conversion: Tips, Tricks & Best Practices',
        category: 'Data Conversion',
        author: 'UmbrellaCo Team',
        date: 'February 3, 2026',
        readTime: '6 min read',
        image: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=1200',
        content: `
## Why Convert CSV to JSON?

CSV (Comma-Separated Values) is great for spreadsheets, but JSON is the standard for web APIs and modern applications. Converting between these formats is a common developer task.

## Understanding the Conversion

### CSV Structure
\`\`\`
name,age,city
John,30,New York
Jane,25,Los Angeles
\`\`\`

### Equivalent JSON
\`\`\`json
[
  {"name": "John", "age": "30", "city": "New York"},
  {"name": "Jane", "age": "25", "city": "Los Angeles"}
]
\`\`\`

## Common Challenges

### 1. Handling Special Characters

Commas, quotes, and newlines within fields need special handling:

- Fields with commas should be quoted
- Quotes within quoted fields should be escaped
- Newlines in fields require proper parsing

### 2. Data Type Conversion

CSV stores everything as strings. Consider converting:

- Numbers: \`"30"\` → \`30\`
- Booleans: \`"true"\` → \`true\`
- Empty values: \`""\` → \`null\`

### 3. Nested Structures

For complex JSON, you might need column naming conventions:

\`\`\`
user.name,user.address.city
John,New York
\`\`\`

## Best Practices

1. **Validate CSV structure** before conversion
2. **Handle encoding properly** (UTF-8 is recommended)
3. **Consider data types** during conversion
4. **Test with edge cases** (empty fields, special characters)

## Quick Conversion

Use our free [CSV to JSON Converter](/json-tools) for instant, accurate conversions!
    `,
    },
    'json-schema-validation-explained': {
        title: "JSON Schema Validation Explained: A Developer's Guide",
        category: 'JSON',
        author: 'UmbrellaCo Team',
        date: 'January 28, 2026',
        readTime: '10 min read',
        image: 'https://images.unsplash.com/photo-1516116216624-53e697fedbea?w=1200',
        content: `
## What is JSON Schema?

JSON Schema is a vocabulary that allows you to annotate and validate JSON documents. Think of it as a contract that defines the structure your JSON data must follow.

## Why Use JSON Schema?

- **Validate API payloads** before processing
- **Document data structures** for team collaboration
- **Generate forms** from schema definitions
- **Ensure data integrity** across systems

## Basic Schema Example

\`\`\`json
{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "type": "object",
  "properties": {
    "name": {
      "type": "string",
      "minLength": 1
    },
    "age": {
      "type": "integer",
      "minimum": 0
    },
    "email": {
      "type": "string",
      "format": "email"
    }
  },
  "required": ["name", "email"]
}
\`\`\`

## Key Schema Keywords

### Type Validation
- \`"type": "string"\`
- \`"type": "number"\`
- \`"type": "boolean"\`
- \`"type": "array"\`
- \`"type": "object"\`

### String Validation
- \`minLength\`, \`maxLength\`
- \`pattern\` (regex)
- \`format\` (email, uri, date-time)

### Number Validation
- \`minimum\`, \`maximum\`
- \`multipleOf\`

### Object Validation
- \`properties\`
- \`required\`
- \`additionalProperties\`

## Validate Your JSON

Try our [JSON Schema Validator](/json-tools) to validate your data against any schema!
    `,
    },
    'api-testing-best-practices': {
        title: 'API Testing Best Practices for Modern Developers',
        category: 'API',
        author: 'UmbrellaCo Team',
        date: 'January 25, 2026',
        readTime: '7 min read',
        image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1200',
        content: `
## Why API Testing Matters

APIs are the backbone of modern applications. Thorough testing ensures reliability, security, and performance.

## Types of API Tests

### 1. Functional Testing
Verify that endpoints return correct data for valid inputs.

### 2. Integration Testing
Ensure APIs work correctly with other services and databases.

### 3. Performance Testing
Test response times under various load conditions.

### 4. Security Testing
Check for vulnerabilities like SQL injection and authentication bypasses.

## Best Practices

### 1. Test All HTTP Methods
\`\`\`
GET    /api/users        - List users
POST   /api/users        - Create user
PUT    /api/users/1      - Update user
DELETE /api/users/1      - Delete user
\`\`\`

### 2. Validate Response Structure
Always verify the response matches expected schema.

### 3. Test Error Scenarios
- Invalid inputs
- Missing authentication
- Rate limiting
- Server errors

### 4. Use Realistic Test Data
Mock data should represent real-world scenarios.

## Quick API Testing

Use our [API Tools](/api-tools) to test endpoints quickly without writing code!
    `,
    },
    'geojson-mapping-fundamentals': {
        title: 'GeoJSON and Interactive Mapping Fundamentals',
        category: 'Spatial',
        author: 'UmbrellaCo Team',
        date: 'January 20, 2026',
        readTime: '9 min read',
        image: 'https://images.unsplash.com/photo-1524661135-423995f22d0b?w=1200',
        content: `
## What is GeoJSON?

GeoJSON is a format for encoding geographic data structures using JSON. It supports points, lines, polygons, and feature collections.

## GeoJSON Structure

### Point
\`\`\`json
{
  "type": "Feature",
  "geometry": {
    "type": "Point",
    "coordinates": [-73.9857, 40.7484]
  },
  "properties": {
    "name": "Empire State Building"
  }
}
\`\`\`

### Polygon
\`\`\`json
{
  "type": "Feature",
  "geometry": {
    "type": "Polygon",
    "coordinates": [[
      [-73.99, 40.73],
      [-73.98, 40.73],
      [-73.98, 40.74],
      [-73.99, 40.74],
      [-73.99, 40.73]
    ]]
  }
}
\`\`\`

## Working with GeoJSON

1. **Create** features using our [Spatial Tools](/spatial-tools-app)
2. **Edit** coordinates directly in the GeoJSON editor
3. **Visualize** on an interactive map
4. **Export** to various formats (KML, GPX)

## Best Practices

- Always close polygons (first and last coordinate must match)
- Use valid coordinate order: [longitude, latitude]
- Include meaningful properties for features
- Validate GeoJSON before using in applications

Try our [Geospatial Workbench](/spatial-tools-app) to create and edit GeoJSON!
    `,
    },
    'text-encoding-base64-guide': {
        title: 'Text Encoding Demystified: Base64, URL Encoding & More',
        category: 'Text Tools',
        author: 'UmbrellaCo Team',
        date: 'January 15, 2026',
        readTime: '5 min read',
        image: 'https://images.unsplash.com/photo-1504639725590-34d0984388bd?w=1200',
        content: `
## Understanding Text Encoding

Text encoding transforms data into different formats for transmission, storage, or display purposes.

## Common Encoding Types

### Base64
Converts binary data to ASCII text. Common uses:
- Embedding images in HTML/CSS
- Transmitting binary data in JSON
- Basic obfuscation (not encryption!)

\`\`\`
Hello World → SGVsbG8gV29ybGQ=
\`\`\`

### URL Encoding
Encodes special characters for safe URL transmission:

\`\`\`
Hello World! → Hello%20World%21
\`\`\`

### HTML Encoding
Escapes special HTML characters:

\`\`\`
<script> → &lt;script&gt;
\`\`\`

## When to Use Each

| Encoding | Use Case |
|----------|----------|
| Base64 | Binary data in text formats |
| URL | Query parameters, URLs |
| HTML | Displaying user content safely |

## Quick Encoding Tools

Use our [Text Tools](/text-tools-app) for instant encoding and decoding!
    `,
    },
};

function BlogPost() {
    const { postId } = useParams();
    const theme = useTheme();
    const post = blogContent[postId];

    const toc = [];
    if (post) {
        post.content.replace(/^(##|###) (.*)$/gm, (match, level, title) => {
            toc.push({
                level: level.length,
                title,
                id: title.toLowerCase().replace(/[^\w]+/g, '-')
            });
        });
    }

    if (!post) {
        return (
            <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
                <Typography variant="h4">Post not found</Typography>
                <Button component={Link} to="/blog" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
                    Back to Blog
                </Button>
            </Container>
        );
    }

    const breadcrumbSchema = createBreadcrumbSchema([
        { name: 'Home', url: 'https://umbrellaco.in' },
        { name: 'Blog', url: 'https://umbrellaco.in/blog' },
        { name: post.title, url: `https://umbrellaco.in/blog/${postId}` }
    ]);

    return (
        <Box sx={{ minHeight: '125vh', bgcolor: 'background.default' }}>
            <SEO
                title={`${post.title} | UmbrellaCo Blog`}
                description={post.content.substring(0, 160).replace(/[#*`\n]/g, '')}
                keywords={`${post.category.toLowerCase()}, developer tutorial, ${post.title.toLowerCase()}`}
                url={`/blog/${postId}`}
                image={post.image}
                jsonLd={breadcrumbSchema}
            />

            {/* Hero Image */}
            <Box
                sx={{
                    height: { xs: 250, md: 400 },
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundImage: `url(${post.image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'brightness(0.6) contrast(1.2)',
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.2) 60%, transparent 100%)',
                    },
                    display: 'flex',
                    alignItems: 'flex-end',
                }}
            >
                <Container maxWidth="md" sx={{ pb: 6, position: 'relative', zIndex: 1 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <Chip label={post.category} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.9), color: 'white', mb: 3, fontWeight: 700 }} />
                        <Typography variant="h2" sx={{ color: 'white', fontWeight: 900, mb: 3, fontSize: { xs: '2rem', md: '3.5rem' }, lineHeight: 1.1 }}>
                            {post.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 4, color: 'rgba(255,255,255,0.8)', flexWrap: 'wrap', fontWeight: 600 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon fontSize="small" />
                                {post.author}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarTodayIcon fontSize="small" />
                                {post.date}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccessTimeIcon fontSize="small" />
                                {post.readTime}
                            </Box>
                        </Box>
                    </motion.div>
                </Container>
            </Box>

            {/* Content */}
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Button
                    component={Link}
                    to="/blog"
                    startIcon={<ArrowBackIcon />}
                    sx={{ mb: 3 }}
                >
                    Back to Blog
                </Button>

                <Grid container spacing={4}>
                    <Grid item xs={12} md={8}>
                        <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#fff', 0.1) : alpha(theme.palette.divider, 0.5)}`, bgcolor: 'background.paper', mt: { xs: 0, md: -6 }, position: 'relative', zIndex: 2, boxShadow: theme.shadows[10] }}>
                            <Box
                                sx={{
                                    '& h2': {
                                        mt: 5,
                                        mb: 3,
                                        fontWeight: 800,
                                        fontSize: '2rem',
                                        color: 'text.primary',
                                    },
                                    '& h3': { mt: 4, mb: 2, fontWeight: 700, fontSize: '1.5rem', color: 'text.primary' },
                                    '& p': { mb: 3, lineHeight: 1.8, fontSize: '1.1rem', color: 'text.secondary' },
                                    '& code': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark,
                                        px: 1,
                                        py: 0.2,
                                        borderRadius: 1,
                                        fontFamily: 'monospace',
                                        fontWeight: 600,
                                    },
                                    '& pre': {
                                        bgcolor: theme.palette.mode === 'dark' ? '#0f172a' : '#f8fafc',
                                        border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#fff', 0.1) : alpha(theme.palette.primary.main, 0.1)}`,
                                        p: 3,
                                        borderRadius: 2,
                                        overflow: 'auto',
                                        mb: 4,
                                        '& code': { bgcolor: 'transparent', px: 0, py: 0, fontWeight: 400, color: 'inherit' },
                                    },
                                    '& a': { color: 'primary.main', textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } },
                                    '& ul, & ol': { pl: 3, mb: 2 },
                                    '& li': { mb: 1 },
                                    '& table': {
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        mb: 2,
                                    },
                                    '& th, & td': {
                                        border: `1px solid ${theme.palette.divider}`,
                                        p: 1,
                                        textAlign: 'left',
                                    },
                                    '& th': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: post.content
                                        .replace(/^(##|###) (.*)$/gm, (match, level, title) => {
                                            const id = title.toLowerCase().replace(/[^\w]+/g, '-');
                                            const tag = level === '##' ? 'h2' : 'h3';
                                            return `<${tag} id="${id}">${title}</${tag}>`;
                                        })
                                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                        .replace(/```(\w+)?\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
                                        .replace(/`([^`]+)`/g, '<code>$1</code>')
                                        .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2">$1</a>')
                                        .replace(/\n\n/g, '</p><p>')
                                        .replace(/^(.+)$/gm, (match) => {
                                            if (match.startsWith('<') || match.startsWith('-') || match.startsWith('|')) return match;
                                            return `<p>${match}</p>`;
                                        })
                                        .replace(/<p><h/g, '<h')
                                        .replace(/<\/h(\d)><\/p>/g, '</h$1>')
                                        .replace(/<p><pre>/g, '<pre>')
                                        .replace(/<\/pre><\/p>/g, '</pre>')
                                }}
                            />
                        </Paper>
                    </Grid>

                    {/* Sticky Table of Contents */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ position: 'sticky', top: 100 }}>
                            <Typography variant="subtitle2" fontWeight={800} sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
                                Table of Contents
                            </Typography>
                            <Box component="nav" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {toc.map((item, index) => (
                                    <Box
                                        key={index}
                                        component="a"
                                        href={`#${item.id}`}
                                        sx={{
                                            textDecoration: 'none',
                                            color: item.level === 2 ? 'text.primary' : 'text.secondary',
                                            fontWeight: item.level === 2 ? 600 : 400,
                                            pl: item.level === 2 ? 0 : 2,
                                            fontSize: item.level === 2 ? '0.95rem' : '0.85rem',
                                            borderLeft: `2px solid ${alpha(theme.palette.primary.main, item.level === 2 ? 0.3 : 0.1)}`,
                                            paddingLeft: item.level === 2 ? '12px' : '20px',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                color: 'primary.main',
                                                borderLeftColor: 'primary.main'
                                            }
                                        }}
                                    >
                                        {item.title}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}

export default BlogPost;
